import { useState } from 'react'
import type { FC } from 'react'
import { Box, Card, CardContent, Chip, LinearProgress, Tab, Tabs, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import type { BffCrmPersonAccumulatorsRetrieveResponse } from '@fireflyhealth/api'

import { EmptyState, Loader } from '~/shared/ui'
import type { PatientUser } from '~/shared/types'
import { PlanYearPicker } from '~/entities/health-plan'
import { useGetPersonAccumulators } from './AccumulatorsService'
import { required } from '~/shared/lib/required'

// Server-owned shapes — reuse the generated response types rather than re-declaring them.
type CoverageLevel = BffCrmPersonAccumulatorsRetrieveResponse['coverage_levels'][number]
type Tier = CoverageLevel['tiers'][number]
type Amount = Tier['deductible']

interface AccumulatorsProps {
  patient: PatientUser
}

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

const formatUsd = (value: number): string => usdFormatter.format(value)

const formatSyncDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })

/** A single deductible / OOP metric: label, $ytd / $max, a "Met" badge, and a progress bar. */
const AmountRow: FC<{ tierKey: string; label: string; amount: Amount }> = ({
  tierKey,
  label,
  amount,
}) => {
  // Presentation-only: the bar's fill %. Whether the amount is "met" is computed server-side
  // (`is_met`) — never re-derived here.
  const percent = amount.max > 0 ? Math.min(100, (amount.ytd / amount.max) * 100) : 0
  return (
    <Box sx={{ mb: 2, '&:last-of-type': { mb: 0 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {amount.is_met && (
            <Chip
              size="small"
              color="success"
              icon={<CheckCircleIcon />}
              label="Met"
              data-testid={`met-${tierKey}-${label}`}
            />
          )}
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {`${formatUsd(amount.ytd)} / ${formatUsd(amount.max)}`}
          </Typography>
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percent}
        color={amount.is_met ? 'success' : 'primary'}
        aria-label={`${label} progress`}
        sx={{ height: 8, borderRadius: 1 }}
      />
    </Box>
  )
}

/** One network tier (e.g. Firefly-Guided) with its deductible and out-of-pocket progress. */
const TierCard: FC<{ tier: Tier }> = ({ tier }) => (
  <Card variant="outlined" sx={{ mb: 2 }} data-testid={`tier-${tier.key}`}>
    <CardContent>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {tier.name}
      </Typography>
      {tier.description && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
          {tier.description}
        </Typography>
      )}
      <AmountRow tierKey={tier.key} label="Deductible" amount={tier.deductible} />
      <AmountRow tierKey={tier.key} label="Out-of-pocket max" amount={tier.out_of_pocket} />
      {tier.cascade_note && (
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', display: 'block', mt: 1.5, fontStyle: 'italic' }}
        >
          {tier.cascade_note}
        </Typography>
      )}
    </CardContent>
  </Card>
)

/** The Individual / Family levels of the year on screen, each as its tiers. */
const CoverageLevels: FC<{ data: BffCrmPersonAccumulatorsRetrieveResponse }> = ({ data }) => {
  const [activeTab, setActiveTab] = useState(0)
  const coverageLevels = data.coverage_levels

  if (coverageLevels.length === 0) {
    return (
      <EmptyState
        icon={<InfoOutlinedIcon />}
        title="No accumulator data"
        caption="This plan year has no deductible or out-of-pocket progress to display, either because accumulators have not synced or its plan maximums are not configured."
      />
    )
  }

  // The Family tab appears only when a family coverage level is returned, so the number of tabs can
  // shrink between renders; clamp the active index so a stale selection never indexes out of bounds.
  const safeTab = Math.min(activeTab, coverageLevels.length - 1)
  const activeLevel = required(coverageLevels[safeTab], 'the active coverage level')

  return (
    <>
      <Tabs
        value={safeTab}
        onChange={(_event, value: number) => setActiveTab(value)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        aria-label="Coverage level"
      >
        {coverageLevels.map(level => (
          <Tab key={level.type} label={level.label} data-testid={`tab-${level.type}`} />
        ))}
      </Tabs>

      {activeLevel.tiers.map(tier => (
        <TierCard key={tier.key} tier={tier} />
      ))}

      {data.last_synced_at && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
          Last synced {formatSyncDate(data.last_synced_at)}
        </Typography>
      )}
    </>
  )
}

const AccumulatorsTab: FC<{ personId: number | undefined }> = ({ personId }) => {
  // Unset until staff pick a year: the response names the one it answered from.
  const [pickedConfigurationId, setPickedConfigurationId] = useState<number>()
  const {
    data,
    isPending: isLoading,
    isPlaceholderData,
    error,
  } = useGetPersonAccumulators({
    personId,
    policyConfigurationId: pickedConfigurationId,
  })

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Loader inline />
      </Box>
    )
  }

  if (error) {
    return (
      <EmptyState
        icon={<InfoOutlinedIcon />}
        title="Couldn't load accumulators"
        caption="Something went wrong fetching this member's accumulator progress. Please try again."
      />
    )
  }

  const configurations = data?.policy_configurations ?? []

  if (!data || configurations.length === 0) {
    return (
      <EmptyState
        icon={<InfoOutlinedIcon />}
        title="No accumulator data"
        caption="This member has no deductible or out-of-pocket progress to display because no plan configuration was found."
      />
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 2.5 }}>
        <PlanYearPicker
          planYears={configurations}
          selectedPlanYearId={
            pickedConfigurationId ?? data.selected_policy_configuration_id ?? undefined
          }
          onChange={setPickedConfigurationId}
          idPrefix="accumulators"
        />
      </Box>

      {/* `keepPreviousData` holds the year just left while a picked one loads; don't show its numbers under the new year. */}
      {isPlaceholderData ? <Loader inline /> : <CoverageLevels data={data} />}
    </Box>
  )
}

// Keyed remount: the route is keyed by path, so a kept response or picked year would be stale.
const Accumulators: FC<AccumulatorsProps> = ({ patient }) => (
  <AccumulatorsTab key={patient.person?.id} personId={patient.person?.id} />
)

export default Accumulators
